import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useFindFirst } from "@gadgetinc/react";
import {
  Box,
  Container,
  VStack,
  Heading,
  Text,
  Button,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Spinner,
  Divider,
  Badge,
  HStack,
} from "@chakra-ui/react";
import { api } from "../api";

export default function VoiceSimToken() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.matchMedia("(max-width: 768px)").matches);
    };
    
    checkMobile();
    window.addEventListener("resize", checkMobile);
    
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const [{ data: voiceSimSession, fetching, error }] = useFindFirst(
    api.voiceSimSession,
    {
      filter: { token: { equals: token } },
      select: {
        id: true,
        token: true,
        status: true,
        expiresAt: true,
        attemptsAllowed: true,
        attemptsUsed: true,
        isActive: true,
        studentEmail: true,
        metadata: true,
        scenarioId: true,
        sharedBy: {
          firstName: true,
          lastName: true,
        },
      },
    }
  );

  // Loading state
  if (fetching) {
    return (
      <Box minH="100vh" bg="linear-gradient(135deg, #0a1929 0%, #1a202c 100%)" py={20}>
        <Container maxW="container.md">
          <VStack spacing={4}>
            <Spinner size="xl" color="blue.400" />
            <Text color="gray.100">Loading your simulation invitation...</Text>
          </VStack>
        </Container>
      </Box>
    );
  }

  // Error state - network or API error
  if (error) {
    return (
      <Box minH="100vh" bg="linear-gradient(135deg, #0a1929 0%, #1a202c 100%)" py={20}>
        <Container maxW="container.md">
          <Alert
            status="error"
            variant="solid"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            textAlign="center"
            height="200px"
            borderRadius="md"
            bg="red.900"
            color="red.100"
          >
            <AlertIcon boxSize="40px" mr={0} color="red.300" />
            <AlertTitle mt={4} mb={1} fontSize="lg">
              Error Loading Invitation
            </AlertTitle>
            <AlertDescription maxWidth="sm">
              {error?.message || "An error occurred while loading your invitation. Please try again or contact your instructor."}
            </AlertDescription>
          </Alert>
        </Container>
      </Box>
    );
  }

  // Token not found
  if (!voiceSimSession) {
    return (
      <Box minH="100vh" bg="linear-gradient(135deg, #0a1929 0%, #1a202c 100%)" py={20}>
        <Container maxW="container.md">
          <Alert
            status="warning"
            variant="solid"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            textAlign="center"
            height="200px"
            borderRadius="md"
            bg="orange.900"
            color="orange.100"
          >
            <AlertIcon boxSize="40px" mr={0} color="orange.300" />
            <AlertTitle mt={4} mb={1} fontSize="lg">
              Invalid or Expired Invitation
            </AlertTitle>
            <AlertDescription maxWidth="sm">
              This simulation invitation link is invalid or has expired. Please contact your instructor for a new invitation.
            </AlertDescription>
          </Alert>
        </Container>
      </Box>
    );
  }

  // Check if invitation is active
  if (!voiceSimSession.isActive) {
    return (
      <Box minH="100vh" bg="linear-gradient(135deg, #0a1929 0%, #1a202c 100%)" py={20}>
        <Container maxW="container.md">
          <Alert
            status="warning"
            variant="solid"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            textAlign="center"
            height="200px"
            borderRadius="md"
            bg="orange.900"
            color="orange.100"
          >
            <AlertIcon boxSize="40px" mr={0} color="orange.300" />
            <AlertTitle mt={4} mb={1} fontSize="lg">
              Invitation Deactivated
            </AlertTitle>
            <AlertDescription maxWidth="sm">
              This invitation has been deactivated by your instructor. Please contact them for more information.
            </AlertDescription>
          </Alert>
        </Container>
      </Box>
    );
  }

  // Check if invitation has expired
  const now = new Date();
  const expiresAt = new Date(voiceSimSession.expiresAt);
  if (expiresAt < now) {
    return (
      <Box minH="100vh" bg="linear-gradient(135deg, #0a1929 0%, #1a202c 100%)" py={20}>
        <Container maxW="container.md">
          <Alert
            status="warning"
            variant="solid"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            textAlign="center"
            height="200px"
            borderRadius="md"
            bg="orange.900"
            color="orange.100"
          >
            <AlertIcon boxSize="40px" mr={0} color="orange.300" />
            <AlertTitle mt={4} mb={1} fontSize="lg">
              Invitation Expired
            </AlertTitle>
            <AlertDescription maxWidth="sm">
              This invitation expired on {expiresAt.toLocaleDateString()} at {expiresAt.toLocaleTimeString()}. 
              Please contact your instructor for a new invitation.
            </AlertDescription>
          </Alert>
        </Container>
      </Box>
    );
  }

  // Check if all attempts have been used
  if (voiceSimSession.attemptsUsed >= voiceSimSession.attemptsAllowed) {
    return (
      <Box minH="100vh" bg="linear-gradient(135deg, #0a1929 0%, #1a202c 100%)" py={20}>
        <Container maxW="container.md">
          <Alert
            status="info"
            variant="solid"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            textAlign="center"
            height="200px"
            borderRadius="md"
            bg="blue.900"
            color="blue.100"
          >
            <AlertIcon boxSize="40px" mr={0} color="blue.300" />
            <AlertTitle mt={4} mb={1} fontSize="lg">
              All Attempts Used
            </AlertTitle>
            <AlertDescription maxWidth="sm">
              You have used all {voiceSimSession.attemptsAllowed} allowed attempt{voiceSimSession.attemptsAllowed > 1 ? 's' : ''} for this simulation. 
              Please contact your instructor if you need additional attempts.
            </AlertDescription>
          </Alert>
        </Container>
      </Box>
    );
  }

  const metadata = voiceSimSession?.metadata && typeof voiceSimSession.metadata === "object" && !Array.isArray(voiceSimSession.metadata)
    ? voiceSimSession.metadata
    : {};
  const invitedScenarioId = String(searchParams.get("scenarioId") || "").trim();
  const metadataScenarioId = String(metadata.scenarioId || "").trim();
  const effectiveScenarioId = String(voiceSimSession.scenarioId || metadataScenarioId || invitedScenarioId || "").trim();
  const scenarioLabel = metadata.scenarioTitle || (effectiveScenarioId ? `Scenario #${effectiveScenarioId}` : "Assigned Voice Simulation");

  if (!effectiveScenarioId) {
    return (
      <Box minH="100vh" bg="linear-gradient(135deg, #0a1929 0%, #1a202c 100%)" py={20}>
        <Container maxW="container.md">
          <Alert
            status="error"
            variant="solid"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            textAlign="center"
            height="220px"
            borderRadius="md"
            bg="red.900"
            color="red.100"
          >
            <AlertIcon boxSize="40px" mr={0} color="red.300" />
            <AlertTitle mt={4} mb={1} fontSize="lg">
              Scenario Unavailable
            </AlertTitle>
            <AlertDescription maxWidth="sm">
              This invitation is not linked to an available scenario. Please contact your instructor for a new voice simulation link.
            </AlertDescription>
          </Alert>
        </Container>
      </Box>
    );
  }

  // Valid invitation - show scenario info and start button
  const attemptsRemaining = voiceSimSession.attemptsAllowed - voiceSimSession.attemptsUsed;
  const instructorName = `${voiceSimSession.sharedBy?.firstName || ''} ${voiceSimSession.sharedBy?.lastName || ''}`.trim() || 'Your instructor';

  const handleStartSimulation = () => {
    navigate(`/voice-scenario/${encodeURIComponent(effectiveScenarioId)}?voiceSimSessionId=${encodeURIComponent(voiceSimSession.id)}&token=${encodeURIComponent(voiceSimSession.token)}`);
  };

  return (
    <Box minH="100vh" bg="linear-gradient(135deg, #0a1929 0%, #1a202c 100%)" py={10}>
      <Container maxW="container.md">
        <Box bg="gray.800" borderRadius="xl" boxShadow="2xl" overflow="hidden">
          <VStack spacing={0} align="stretch">
            {/* Header Section */}
            <Box bg="gray.900" color="white" py={8} px={6} textAlign="center">
              <Heading size="2xl" mb={3} fontWeight="bold">
                Voice Simulation Invitation
              </Heading>
              <Text fontSize="lg" color="gray.300">
                Shared by {instructorName}
              </Text>
            </Box>

            {/* Main Content */}
            <VStack spacing={6} p={8} align="stretch">
              {/* Scenario Details */}
              <Box 
                bg="gray.700" 
                p={6} 
                borderRadius="lg" 
                borderWidth="1px" 
                borderColor="gray.600"
                boxShadow="sm"
              >
                <VStack spacing={4} align="stretch">
                  <Box>
                    <Heading size="lg" color="gray.100" mb={4}>
                      {scenarioLabel}
                    </Heading>
                    <Text color="gray.400" fontSize="sm">
                      Scenario ID: {effectiveScenarioId}
                    </Text>
                  </Box>

                  <Divider borderColor="gray.600" />

                  <VStack spacing={3} align="stretch">
                    <HStack justify="space-between" flexWrap="wrap">
                      <Text fontWeight="semibold" color="gray.300">Student Email:</Text>
                      <Text color="gray.100">{voiceSimSession.studentEmail}</Text>
                    </HStack>

                    <HStack justify="space-between" flexWrap="wrap">
                      <Text fontWeight="semibold" color="gray.300">Attempts Remaining:</Text>
                      <Badge colorScheme={attemptsRemaining > 1 ? "green" : "orange"} fontSize="md" px={3} py={1}>
                        {attemptsRemaining} of {voiceSimSession.attemptsAllowed}
                      </Badge>
                    </HStack>

                    <HStack justify="space-between" flexWrap="wrap">
                      <Text fontWeight="semibold" color="gray.300">Expires:</Text>
                      <Text color="gray.100">
                        {expiresAt.toLocaleDateString()} at {expiresAt.toLocaleTimeString()}
                      </Text>
                    </HStack>

                    <HStack justify="space-between" flexWrap="wrap">
                      <Text fontWeight="semibold" color="gray.300">Status:</Text>
                      <Badge colorScheme="green" px={3} py={1}>Active</Badge>
                    </HStack>
                  </VStack>
                </VStack>
              </Box>

              {/* Before You Begin Alert */}
              <Alert 
                status="info" 
                borderRadius="lg" 
                bg="blue.900" 
                borderWidth="1px" 
                borderColor="blue.700"
                boxShadow="sm"
              >
                <AlertIcon color="blue.300" />
                <Box>
                  <AlertTitle color="blue.100" fontWeight="bold">Before You Begin</AlertTitle>
                  <AlertDescription color="blue.200" fontSize="sm">
                    Make sure you have a stable internet connection and a quiet environment. 
                    You will be using your microphone to interact with the simulation.
                  </AlertDescription>
                  {isMobile && (
                    <AlertDescription color="blue.200" fontSize="sm" mt={2}>
                      On mobile devices, ensure your device is not on silent mode and volume is turned up. 
                      The first audio may require you to tap the screen to enable sound.
                    </AlertDescription>
                  )}
                </Box>
              </Alert>

              {/* Start Button */}
              <Button
                colorScheme="blue"
                size="lg"
                onClick={handleStartSimulation}
                width="full"
                height="60px"
                fontSize="xl"
                fontWeight="bold"
                boxShadow="md"
                _hover={{ boxShadow: "lg", transform: "translateY(-2px)" }}
                transition="all 0.2s"
              >
                Start Simulation
              </Button>
            </VStack>
          </VStack>
        </Box>
      </Container>
    </Box>
  );
}
